import type { BuildContext, Fragment } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { bigStatementSchema, type BigStatementProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { tl } from "../timeline.js";
import { assemble } from "./shared.js";

export const BIG_STATEMENT_DURATION_SEC = 8;

export function buildBigStatementHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = bigStatementSchema.parse(rawProps) as BigStatementProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: BIG_STATEMENT_DURATION_SEC };

  const numberSize = scaleFor(ctx.format, { base: 360, short: 460, square: 360, wide: 420 });
  const statementSize = scaleFor(ctx.format, { base: 96, short: 110, square: 88, wide: 100 });
  const proofSize = scaleFor(ctx.format, { base: 48, short: 56, square: 44, wide: 52 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const bg = backgroundGradient(fullCtx, "bg", { variant: "dark", animated: true });

  const numberStart = 4 / 30;
  const statementStart = 20 / 30;
  const proofStart = 70 / 30;
  const ctaStart = 180 / 30;

  const numberDur = BIG_STATEMENT_DURATION_SEC - numberStart;
  const statementDur = BIG_STATEMENT_DURATION_SEC - statementStart;
  const proofDur = BIG_STATEMENT_DURATION_SEC - proofStart;
  const ctaDur = 80 / 30;

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });

  // Big number — gradient text with fade-in + slight scale.
  const numberFrag: Fragment | null = props.bigNumber
    ? {
        html: `<div id="tpl-number" style="font-weight:900;font-size:${numberSize}px;line-height:0.95;letter-spacing:-8px;background:linear-gradient(135deg, ${colors.primary}, ${colors.accent});-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;text-align:center;">${escapeHtml(props.bigNumber)}</div>`,
        init: `gsap.set("#tpl-number", { opacity: 0, scale: 0.8, transformOrigin: "center" });`,
        tweens: [
          tl.fromTo(
            "#tpl-number",
            { opacity: 0, scale: 0.8 },
            { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.4)" },
            numberStart,
          ),
        ],
      }
    : null;

  const statementFrag = hookText({
    id: "tpl-statement",
    text: props.statement,
    startSec: statementStart,
    size: statementSize,
    color: "#fff",
    align: "center",
  });

  const proofFrag: Fragment | null = props.proof
    ? {
        html: `<div id="tpl-proof" style="font-weight:600;font-size:${proofSize}px;color:${colors.muted};text-align:center;text-wrap:balance;">${escapeHtml(props.proof)}</div>`,
        init: `gsap.set("#tpl-proof", { opacity: 0, y: 20 });`,
        tweens: [
          tl.fromTo(
            "#tpl-proof",
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
            proofStart,
          ),
        ],
      }
    : null;

  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div id="tpl-center" style="flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;gap:24px;">
  ${numberFrag ? `<div id="tpl-number-clip" class="clip" data-start="${numberStart}" data-duration="${numberDur}" data-track-index="0">${numberFrag.html}</div>` : ""}
  <div id="tpl-statement-clip" class="clip" data-start="${statementStart}" data-duration="${statementDur}" data-track-index="1">${statementFrag.html}</div>
  ${proofFrag ? `<div id="tpl-proof-clip" class="clip" data-start="${proofStart}" data-duration="${proofDur}" data-track-index="2">${proofFrag.html}</div>` : ""}
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="3" style="display:flex;justify-content:center;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  const fragments: Fragment[] = [logoFrag, statementFrag, ctaFrag];
  if (numberFrag) fragments.push(numberFrag);
  if (proofFrag) fragments.push(proofFrag);

  return assemble({
    templateId: "BigStatement",
    ctx: fullCtx,
    background: bg,
    contentFragments: fragments,
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
